import { Fragment } from 'react';

import Link from 'next/link';

import type { MarketingNavLink, MarketingNavSubItem } from '../config/marketing-nav';
import { NavColumnsPanel } from './nav-columns-panel';
import { ProductNavPanel } from './product-nav-panel';

/**
 * The default popover body: a list of sub-links, each a boxed icon + label +
 * description. Items carrying a `group` render a small uppercase header
 * wherever the group changes (e.g. the "For companies" / "For individuals"
 * split in Use cases).
 */
const NavItemList = ({ items }: { items: readonly MarketingNavSubItem[] }) => (
	<div className="p-2">
		{items.map((item, index) => {
			const Icon = item.icon;
			const showGroup = !!item.group && item.group !== items[index - 1]?.group;
			return (
				<Fragment key={item.href}>
					{showGroup && (
						<span className="mt-1.5 block px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-white/35 first:mt-0">
							{item.group}
						</span>
					)}
					<Link
						href={item.href}
						role="menuitem"
						className="flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-white/5"
					>
						{Icon && (
							<span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.08] text-white">
								<Icon className="size-[18px]" />
							</span>
						)}
						<span className="min-w-0">
							<span className="block text-[14px] font-medium text-white">{item.label}</span>
							{item.description && (
								<span className="mt-0.5 block text-[12.5px] leading-snug text-white/45">
									{item.description}
								</span>
							)}
						</span>
					</Link>
				</Fragment>
			);
		})}
	</div>
);

/**
 * MarketingNavPopover - the inner content of the shared header nav popover.
 * A link's `kind` selects a designed body (the Product card-in-card stage grid
 * or the group-column panel Use cases and Resources share); links without one
 * get the icon-row list. Presentational - the traveling/crossfading/
 * height-morphing shell lives in MarketingNavLinks.
 */
export const MarketingNavPopover = ({ link }: { link: MarketingNavLink }) => {
	if (link.kind === 'product') return <ProductNavPanel />;
	if (link.kind === 'columns') {
		return (
			<NavColumnsPanel
				items={link.items ?? []}
				footer={link.panelFooter}
				columnCount={link.panelColumns}
			/>
		);
	}
	return <NavItemList items={link.items ?? []} />;
};
